import { logger } from "./logger.js";

const API_URL =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  "http://localhost:4000/api";

try {
  logger.info("Using API base URL", { baseUrl: API_URL });
} catch {}

const STORAGE_KEYS = {
  PRODUCTS: "streaming_products",
  POPUP_CONFIG: "popup_config",
  WHATSAPP_CONFIG: "whatsapp_config",
  ADMIN_AUTH: "admin_auth",
};

// Initial data (for Fallback)
const initialData = {
  products: [
    {
      id: "1",
      name: "Netflix Premium",
      description: "Acceso completo a Netflix con 4 pantallas simultáneas",
      imageUrl:
        "https://images.pexels.com/photos/1040159/pexels-photo-1040159.jpeg?auto=compress&cs=tinysrgb&w=400",
      originalPrice: 50000,
      discountPrice: 25000,
      minOrder: 1,
      isActive: true,
      category: "alquilados",
      tags: ["POPULAR", "HOT"],
    },
    {
      id: "2",
      name: "Spotify Premium",
      description: "Música sin anuncios, calidad alta y descargas offline",
      imageUrl:
        "https://images.pexels.com/photos/167092/pexels-photo-167092.jpeg?auto=compress&cs=tinysrgb&w=400",
      originalPrice: 30000,
      discountPrice: 15000,
      minOrder: 1,
      isActive: true,
      category: "dominio",
      tags: ["BEST_SELLER"],
    },
    {
      id: "3",
      name: "Disney+ Premium",
      description: "Todo el contenido de Disney, Marvel, Star Wars y más",
      imageUrl:
        "https://images.pexels.com/photos/5082566/pexels-photo-5082566.jpeg?auto=compress&cs=tinysrgb&w=400",
      originalPrice: 40000,
      discountPrice: 20000,
      minOrder: 1,
      isActive: true,
      category: "alquilados",
      tags: ["NEW", "RECOMMENDED"],
    },
  ],
  popupConfig: {
    isActive: false,
    imageUrl: "",
    message: "",
    cooldownMinutes: 30,
  },
  whatsappConfig: {
    phoneNumber: "+573009734435",
    baseMessage: "¡Hola! Estoy interesado en sus servicios de streaming.",
  },
};

class DataService {
  constructor() {
    this.initializeData();
  }

  initializeData() {
    // Force update products with new properties
    const existingProducts = localStorage.getItem(STORAGE_KEYS.PRODUCTS);

    if (!existingProducts) {
      this.setProducts(initialData.products);
    } else {
      // Check if existing products have new properties
      const products = JSON.parse(existingProducts);
      const needsUpdate = products.some(
        (p) => !p.hasOwnProperty("category") || !p.hasOwnProperty("tags")
      );

      if (needsUpdate) {
        logger.info("Actualizando productos con nuevas propiedades...");
        this.setProducts(initialData.products);
      }
    }

    if (!localStorage.getItem(STORAGE_KEYS.POPUP_CONFIG)) {
      this.setPopupConfig(initialData.popupConfig);
    }
    if (!localStorage.getItem(STORAGE_KEYS.WHATSAPP_CONFIG)) {
      this.setWhatsAppConfig(initialData.whatsappConfig);
    }
  }

  // Products
  async getProducts() {
    try {
      const response = await fetch(`${API_URL}/products`);
      if (!response.ok) throw new Error("Error al obtener productos");
      return await response.json();
    } catch (error) {
      logger.warning("API not available, using localStorage as fallback", {
        error: error.message,
      });
      const products = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      return products ? JSON.parse(products) : [];
    }
  }

  async getActiveProducts() {
    try {
      const response = await fetch(`${API_URL}/products/active`);
      if (!response.ok) throw new Error("Error al obtener productos activos");
      return await response.json();
    } catch (error) {
      logger.warning("API not available, using localStorage as fallback", {
        error: error.message,
      });
      return this.getProducts().then((products) =>
        products.filter((product) => product.isActive)
      );
    }
  }

  async getProductsByCategory(category) {
    try {
      const response = await fetch(`${API_URL}/products/category/${category}`);
      if (!response.ok)
        throw new Error("Error al obtener productos por categoría");
      return await response.json();
    } catch (error) {
      logger.warning("API not available, using localStorage as fallback", {
        error: error.message,
        category,
      });
      const products = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      const allProducts = products ? JSON.parse(products) : [];
      return allProducts.filter(
        (product) => product.category === category.toUpperCase()
      );
    }
  }

  setProducts(products) {
    localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(products));
  }

  async addProduct(product) {
    try {
      const response = await fetch(`${API_URL}/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(product),
      });
      if (!response.ok) throw new Error("Error al crear producto");
      return await response.json();
    } catch (error) {
      logger.warning("API not available, saving product to localStorage", {
        error: error.message,
        productName: product.name,
      });
      const products = await this.getProducts();
      const newProduct = {
        ...product,
        id: Date.now().toString(),
        isActive: true,
      };
      products.push(newProduct);
      this.setProducts(products);
      return newProduct;
    }
  }

  async updateProduct(id, updates) {
    try {
      const response = await fetch(`${API_URL}/products/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error("Error al actualizar producto");
      return await response.json();
    } catch (error) {
      logger.warning("API not available, updating product in localStorage", {
        error: error.message,
        productId: id,
      });
      const products = await this.getProducts();
      const index = products.findIndex((p) => p.id === id);
      if (index !== -1) {
        products[index] = { ...products[index], ...updates };
        this.setProducts(products);
        return products[index];
      }
      return null;
    }
  }

  async deleteProduct(id) {
    try {
      const response = await fetch(`${API_URL}/products/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Error al eliminar producto");
      return await response.json();
    } catch (error) {
      logger.warning("API not available, deleting product from localStorage", {
        error: error.message,
        productId: id,
      });
      const products = await this.getProducts();
      const filteredProducts = products.filter((p) => p.id !== id);
      this.setProducts(filteredProducts);
    }
  }

  // Popup configuration
  async getPopupConfig() {
    try {
      const response = await fetch(`${API_URL}/popup`);
      if (!response.ok)
        throw new Error("Error al obtener configuración de popup");
      return await response.json();
    } catch (error) {
      logger.warning(
        "API not available, using popup configuration from localStorage",
        { error: error.message }
      );
      const config = localStorage.getItem(STORAGE_KEYS.POPUP_CONFIG);
      return config ? JSON.parse(config) : initialData.popupConfig;
    }
  }

  async setPopupConfig(config) {
    try {
      logger.debug("Sending popup configuration to backend", { config });

      // Obtener el token correctamente
      const authData = localStorage.getItem(STORAGE_KEYS.ADMIN_AUTH);
      const token = authData ? JSON.parse(authData).token : null;

      if (!token) {
        throw new Error("No hay token de autenticación disponible");
      }

      const response = await fetch(`${API_URL}/popup`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error("Error in server response while updating popup", {
          status: response.status,
          errorText,
        });
        throw new Error(
          `Error updating popup configuration: ${response.status}`
        );
      }

      const result = await response.json();
      logger.success("Popup configuration updated successfully", {
        result,
      });
      return result;
    } catch (error) {
      logger.warning(
        "Error updating popup in API, using localStorage as fallback",
        { error: error.message, config }
      );
      localStorage.setItem(STORAGE_KEYS.POPUP_CONFIG, JSON.stringify(config));
      return config;
    }
  }

  // WhatsApp configuration
  async getWhatsAppConfig() {
    try {
      const response = await fetch(`${API_URL}/whatsapp`);
      if (!response.ok)
        throw new Error("Error al obtener configuración de WhatsApp");
      return await response.json();
    } catch (error) {
      logger.warning(
        "API not available, using WhatsApp configuration from localStorage"
      );
      const config = localStorage.getItem(STORAGE_KEYS.WHATSAPP_CONFIG);
      return config ? JSON.parse(config) : initialData.whatsappConfig;
    }
  }

  async setWhatsAppConfig(config) {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.ADMIN_AUTH);
      const response = await fetch(`${API_URL}/whatsapp`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(config),
      });
      if (!response.ok)
        throw new Error("Error al actualizar configuración de WhatsApp");
      return await response.json();
    } catch (error) {
      logger.warning(
        "API not available, saving WhatsApp configuration to localStorage",
        { error: error.message, config }
      );
      localStorage.setItem(
        STORAGE_KEYS.WHATSAPP_CONFIG,
        JSON.stringify(config)
      );
      return config;
    }
  }

  // Admin Authentication
  async login(credentials) {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error al iniciar sesión");
      }

      const authData = await response.json();
      this.setAdminAuth(authData);
      return authData;
    } catch (error) {
      logger.error("Error authenticating", {
        error: error.message,
        username: credentials.username,
      });
      throw error;
    }
  }

  setAdminAuth(credentials) {
    localStorage.setItem(STORAGE_KEYS.ADMIN_AUTH, JSON.stringify(credentials));
  }

  getAdminAuth() {
    const auth = localStorage.getItem(STORAGE_KEYS.ADMIN_AUTH);
    return auth ? JSON.parse(auth) : null;
  }

  isAdminAuthenticated() {
    const auth = this.getAdminAuth();
    return auth && auth.isAuthenticated;
  }

  logoutAdmin() {
    localStorage.removeItem(STORAGE_KEYS.ADMIN_AUTH);
  }

  async getAlquiladosProducts() {
    try {
      const response = await fetch(`${API_URL}/products/alquilados`);
      if (!response.ok)
        throw new Error("Error al obtener productos alquilados");
      return await response.json();
    } catch (error) {
      logger.warning(
        "API not available, getting rented products from localStorage",
        { error: error.message }
      );
      const products = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      const allProducts = products ? JSON.parse(products) : [];

      // Filter IsActive for main pages
      return allProducts.filter(
        (p) =>
          (p.category === "alquilados" || p.category === "ALQUILADOS") &&
          p.isActive === true
      );
    }
  }

  async getDominioProducts() {
    try {
      const response = await fetch(`${API_URL}/products/dominio`);
      if (!response.ok) throw new Error("Error al obtener productos a dominio");
      return await response.json();
    } catch (error) {
      logger.warning(
        "API not available, getting dominio products from localStorage",
        { error: error.message }
      );
      const products = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      const allProducts = products ? JSON.parse(products) : [];

      // Filter IsActive for main pages
      return allProducts.filter(
        (p) =>
          (p.category === "dominio" || p.category === "DOMINIO") &&
          p.isActive === true
      );
    }
  }

  async getPerfilesProducts() {
    try {
      const response = await fetch(`${API_URL}/products/perfiles`);
      if (!response.ok)
        throw new Error("Error al obtener productos perfiles");
      return await response.json();
    } catch (error) {
      logger.warning(
        "API not available, getting perfiles products from localStorage",
        { error: error.message }
      );
      const products = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      const allProducts = products ? JSON.parse(products) : [];

      return allProducts.filter(
        (p) =>
          (p.category === "perfiles" || p.category === "PERFILES") &&
          p.isActive === true
      );
    }
  }
}

export const dataService = new DataService();
